/* eslint-disable react-hooks/rules-of-hooks */
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { cmsApi, internalApi } from "@Utils/httpclient";

import hookCollectionStoreFactory from "./store-collection";
import hookCollectionsStoreFactory from "./store-collections";
import hookPivotTableStoreFactory from "./store-pivot-table-report";
import hookReportStoreFactory from "./store-report";
import hookReportViewStoreFactory from "./store-report-view";
import hookVersionStoreFactory from "./store-version";

function hookBaseFactory() {
  return () => {
    const [loading, setLoading] = useState(false)

    const fetch = useCallback(async ({ method = "get", url = "", ...payload }) => {
      setLoading(true)
      const { data } = await internalApi.request({
        method,
        url,
        ...payload,
      }).finally(() => {
        setLoading(false)
      })

      return data
    }, [])

    const fetchCms = useCallback(async ({ method = "get", url = "", ...payload }) => {
      setLoading(true)
      const { data } = await cmsApi.request({
        method,
        url,
        ...payload,
      }).finally(() => {
        setLoading(false)
      })

      return data
    }, [])

    return useMemo(() => ({
      loading, fetch, fetchCms,
    }), [loading, fetch, fetchCms])
  }
}

/**
 * @template TStore
 */
class StoreFactory {
  /**
   * @type {function():TStore}
   */
  useStoreFactory() { }

  /**
   * @template TExtendingStore
   * @param {function(TStore): TExtendingStore} useExtendedStore
   * @return {StoreFactory<TStore&TExtendingStore>}
   */
  extend(useExtendedStore) {
    return new class extends this.constructor {
      useStoreFactory() {
        const store = super.useStoreFactory()
        const extended = useExtendedStore(store)

        return useMemo(() => ({ ...store, ...extended }), [store, extended])
      }
    }
  }

  factory() {
    /**
     * @type {TStore}
     */
    const context = {}
    const Context = createContext(context)
    const useStore = () => useContext(Context)

    const Provider = ({ children }) => {
      const store = this.useStoreFactory()

      return <Context.Provider value={store}>
        {children}
      </Context.Provider>
    }

    return {
      useStore,
      Provider,
    }
  }

  /**
   * @template {string} T
   * @param {T} collectionURL
   */

  extendHookCollectionFactory(collectionURL, sendSiteId = true, clearPayload) {
    return this.extend(hookCollectionStoreFactory(collectionURL, { sendSiteId, clearPayload }))
  }

  /**
   * @template {string} T
   * @param {T} collectionsURL
   */

  extendHookCollectionsFactory(collectionsURL, sendSiteId = true) {
    return this.extend(hookCollectionsStoreFactory(collectionsURL, { sendSiteId }))
  }

  /**
   * @template {string} T
   * @param {T} collectionsURL
   */

  extendHookPivotTableStoreFactory(collectionsURL) {
    return this.extend(hookPivotTableStoreFactory(collectionsURL))
  }

  /**
   * @template {string} T
   * @param {T} siteKey
   */

  extendReportFactory() {
    return this.extend(hookReportStoreFactory())
  }

  /**
   * @template {string} T
   * @param {T} reportType
   */

  extendReportViewFactory(reportType) {
    return this.extend(hookReportViewStoreFactory(reportType))
  }

  /**
   * @template {string} T
   * @param {T} key
   */

  extendHookVersionFactory(key) {
    return this.extend(hookVersionStoreFactory(key))
  }
}

/**
 * @template BaseStore
 * @param {function(): BaseStore} useBaseStore
 * @return {StoreFactory<BaseStore>}
 */
function createStore(useBaseStore) {
  return new class extends StoreFactory {
    useStoreFactory() {
      return useBaseStore()
    }
  }
}

export default (baseFactory = hookBaseFactory) => createStore(baseFactory())
